/**
 * Tenant pricelist catalogue for the Setup pickers.
 *
 * Server mode: `GET /api/repricing/setup/pricelists` (dim_pricelist synced by
 * pms-adapter) — the UI keeps working with `code`, the adapters map code ↔ id.
 * Mock mode: the static Canarias snapshot in `@mocks/catalog.js` (no ids →
 * `pricelistMaps` is empty, which is fine because nothing is sent anywhere).
 *
 * Provided once by `Setup/index.jsx`; pickers read it with `usePricelists()`.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { PRICELISTS as MOCK_PRICELISTS } from 'Pages/DsRepricing/Setup/@mocks/catalog'
import { pricelistMaps } from './adapters'
import { setupApi } from './api'
import { USE_MOCKS } from './flag'

const EMPTY = { items: MOCK_PRICELISTS, maps: pricelistMaps([]), loading: false, error: null, reload: () => {} }
const PricelistCatalog = createContext(EMPTY)

export function PricelistCatalogProvider({ children }) {
	const [state, setState] = useState(USE_MOCKS ? EMPTY : { ...EMPTY, items: [], loading: true })
	// `tick` reruns the fetch: the Retry of a failed catalogue reloads the
	// request, not the page (CU-869fhuhy6, UX v3 D16-e).
	const [tick, setTick] = useState(0)
	const reload = useCallback(() => setTick(t => t + 1), [])

	useEffect(() => {
		if (USE_MOCKS) return
		let alive = true
		setState(s => ({ ...s, loading: true, error: null }))
		setupApi.pricelists()
			.then(items => alive && setState({
				items: items.map(p => ({ id: p.id, code: p.code, name: p.name || p.code, currency: null,
					source_id: p.source_id, base_pricelist_id: p.base_pricelist_id,
					tiers: p.tiers || [] })),
				maps: pricelistMaps(items), loading: false, error: null }))
			.catch(error => alive && setState({ items: [], maps: pricelistMaps([]), loading: false, error }))
		return () => { alive = false }
	}, [tick])

	const value = useMemo(() => ({ ...state, reload }), [state, reload])
	return <PricelistCatalog.Provider value={value}>{children}</PricelistCatalog.Provider>
}

/** `[{code, name, …}]` — drop-in for the old static `PRICELISTS` import. */
export function usePricelists() {
	return useContext(PricelistCatalog).items
}

/**
 * Canonical tiers of a pricelist by code (`dim_pricelist_tier`). Empty in
 * mock mode — the static snapshot has no tier structure, so the editor
 * says so instead of inventing brackets.
 */
export function usePricelistTiers(code) {
	const { items } = useContext(PricelistCatalog)
	return useMemo(() => (code && items.find(p => p.code === code)?.tiers) || [], [items, code])
}

/**
 * Code of the pricelist this one is derived from, or null when it is not
 * PMS-derived. `base_pricelist_id` holds the PMS's own id, so it is matched
 * against `source_id` — that is how "Add compensator" knows its target
 * without a request per face (PRD 01 §3 "Pricelists heredadas del PMS").
 */
export function useCompensatorTarget(code) {
	const { items } = useContext(PricelistCatalog)
	return useMemo(() => {
		const pl = code && items.find(p => p.code === code)
		if (!pl?.base_pricelist_id) return null
		return items.find(p => p.source_id === pl.base_pricelist_id)?.code || null
	}, [items, code])
}

export function usePricelistCatalog() {
	return useContext(PricelistCatalog)
}
